// Shared derivations over the canonical configuration-completeness signal — the KB-derived
// endpoints `GET /buildings/{id}/completeness` (one building) and `/buildings/completeness-summary`
// (the portfolio rollup). Both the single-building Konfiguraatio tab and the portfolio
// Konfiguraatio view read coverage and stage from here, so the two can never disagree about the
// same building. See docs/spec/concepts.md § Configuration completeness.

import type { BuildingCompletenessSummary } from "@/lib/api/agent";

export type Stage = "ready" | "partial" | "onboarding";

export const stageLabel: Record<Stage, string> = {
  ready: "valmis",
  partial: "osittain",
  onboarding: "käyttöönotossa",
};

/** Share of a building's equipment items that are fully configured, 0..100 %. */
export function coveragePercent(rollup: BuildingCompletenessSummary): number {
  return rollup.total_items > 0
    ? (rollup.configured_items / rollup.total_items) * 100
    : 0;
}

/**
 * Onboarding stage of one building. "configured" is the only fully-set-up state; "needs_attention"
 * with some items done is partial, with none (or no equipment at all) it is still onboarding — per
 * the concepts.md rollup.
 */
export function completenessStage(rollup: BuildingCompletenessSummary): Stage {
  if (rollup.status === "configured") return "ready";
  return rollup.configured_items > 0 ? "partial" : "onboarding";
}

// The six completeness criteria an item is checked against, matching the ids the API reports in
// `ItemCompleteness.unmet` (services/agent/.../knowledge_base/completeness.py).
export const CRITERION_TYPED = "typed";
export const CRITERION_VARIANTS = "variants";
export const CRITERION_DATA_POINTS = "data_points";
export const CRITERION_CONFIG = "config";
export const CRITERION_ZONE = "zone";
export const CRITERION_DEPENDENCIES = "dependencies";

const CRITERION_LABEL: Record<string, string> = {
  [CRITERION_TYPED]: "tyypitys puuttuu",
  [CRITERION_VARIANTS]: "variantit valitsematta",
  [CRITERION_DATA_POINTS]: "datapisteitä puuttuu",
  [CRITERION_CONFIG]: "asetusparametreja puuttuu",
  [CRITERION_ZONE]: "vyöhykelinkitys puuttuu",
  [CRITERION_DEPENDENCIES]: "riippuvuudet puuttuvat",
};

/**
 * Finnish label for a criterion id from `unmet`. Falls back to the raw id: the criteria are
 * KB-derived and the API may report one this build has no label for.
 */
export function criterionLabel(id: string): string {
  return CRITERION_LABEL[id] ?? id;
}
